import React, { useState, useEffect } from 'react'
import {X, Plus, Edit2, Trash2, Save, Star, Upload} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface TestimonialsManagerProps {
  onClose: () => void
  embedded?: boolean
}

interface Testimonial {
  _id: string
  firstName: string
  position: string
  company: string
  testimonial: string
  rating?: number
  isVisible: boolean
  logo?: string
  website?: string
  createdAt: string
  updatedAt: string
}

export function TestimonialsManager({ onClose, embedded = false }: TestimonialsManagerProps) {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    position: '',
    company: '',
    testimonial: '',
    rating: 5,
    isVisible: true,
    logo: '',
    website: ''
  })
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    fetchTestimonials()
  }, [])

  const fetchTestimonials = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.testimonials.list({
        sort: { createdAt: -1 }
      })
      setTestimonials(list || [])
    } catch (error) {
      console.error('Erreur lors du chargement des témoignages:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const firstResult = results[0]
      
      if (firstResult.fileUrl) {
        setFormData({ ...formData, logo: firstResult.fileUrl })
      } else if (firstResult.uploadError) {
        alert(`Erreur d'upload: ${firstResult.uploadError}`)
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error)
      alert('Erreur lors de l\'upload du logo')
    } finally {
      setUploading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await lumi.entities.testimonials.create({
        ...formData,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      setShowAddForm(false)
      setFormData({ firstName: '', position: '', company: '', testimonial: '', rating: 5, isVisible: true, logo: '', website: '' })
      await fetchTestimonials()
    } catch (error) {
      console.error('Erreur lors de la création:', error)
      alert('Erreur lors de la création du témoignage')
    }
  }

  const handleUpdate = async (id: string, updates: Partial<Testimonial>) => {
    try {
      await lumi.entities.testimonials.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      setEditingId(null)
      await fetchTestimonials()
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error)
      alert('Erreur lors de la mise à jour')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce témoignage ?')) return
    
    try {
      await lumi.entities.testimonials.delete(id)
      await fetchTestimonials()
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      alert('Erreur lors de la suppression')
    }
  }

  const toggleVisibility = async (testimonial: Testimonial) => {
    await handleUpdate(testimonial._id, { isVisible: !testimonial.isVisible })
  }

  if (!embedded) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center rounded-t-2xl z-10">
            <h2 className="text-2xl font-bold text-gray-900">Gestion des témoignages</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <div className="p-6">
            <TestimonialsContent
              testimonials={testimonials}
              loading={loading}
              editingId={editingId}
              setEditingId={setEditingId}
              showAddForm={showAddForm}
              setShowAddForm={setShowAddForm}
              formData={formData}
              setFormData={setFormData}
              handleCreate={handleCreate}
              handleUpdate={handleUpdate}
              handleDelete={handleDelete}
              toggleVisibility={toggleVisibility}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <TestimonialsContent
      testimonials={testimonials}
      loading={loading}
      editingId={editingId}
      setEditingId={setEditingId}
      showAddForm={showAddForm}
      setShowAddForm={setShowAddForm}
      formData={formData}
      setFormData={setFormData}
      handleCreate={handleCreate}
      handleUpdate={handleUpdate}
      handleDelete={handleDelete}
      toggleVisibility={toggleVisibility}
    />
  )
}

function TestimonialsContent({
  testimonials,
  loading,
  editingId,
  setEditingId,
  showAddForm,
  setShowAddForm,
  formData,
  setFormData,
  handleCreate,
  handleUpdate,
  handleDelete,
  toggleVisibility
}: any) {
  const [editForm, setEditForm] = useState<any>({})
  const [uploadingEdit, setUploadingEdit] = useState(false)

  const handleEditLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploadingEdit(true)
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const firstResult = results[0]
      
      if (firstResult.fileUrl) {
        setEditForm({ ...editForm, logo: firstResult.fileUrl })
      } else if (firstResult.uploadError) {
        alert(`Erreur d'upload: ${firstResult.uploadError}`)
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error)
      alert('Erreur lors de l\'upload du logo')
    } finally {
      setUploadingEdit(false)
    }
  }

  const startEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial._id)
    setEditForm({
      firstName: testimonial.firstName,
      position: testimonial.position,
      company: testimonial.company,
      testimonial: testimonial.testimonial,
      rating: testimonial.rating || 5,
      isVisible: testimonial.isVisible,
      logo: testimonial.logo || '',
      website: testimonial.website || ''
    })
  }

  const saveEdit = async (id: string) => {
    await handleUpdate(id, editForm)
    setEditForm({})
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        <p className="text-gray-600 mt-4">Chargement...</p>
      </div>
    )
  }

  return (
    <div>
      {/* Add Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter un témoignage
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Nouveau témoignage</h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  required
                  placeholder="Sophie"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Poste *</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  required
                  placeholder="Directrice Générale"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Entreprise *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  required
                  placeholder="Tech Solutions SA"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Logo</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-600 transition-colors">
                        <Upload className="w-5 h-5 text-gray-500" />
                        <span className="text-sm text-gray-600">{uploading ? 'Upload en cours...' : 'Uploader un logo'}</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  {formData.logo && (
                    <div className="flex items-center gap-2">
                      <img src={formData.logo} alt="Logo" className="w-12 h-12 object-contain rounded border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo: '' })}
                        className="text-red-600 text-sm hover:underline"
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Site web</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  placeholder="https://exemple.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Témoignage *</label>
              <textarea
                value={formData.testimonial}
                onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none resize-none"
                rows={4}
                required
                placeholder="Partagez votre expérience avec RUGIR..."
              />
            </div>

            <div className="flex items-center gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Note (sur 5)</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= formData.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isVisible"
                  checked={formData.isVisible}
                  onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                  className="w-5 h-5 text-teal-600 rounded focus:ring-teal-600"
                />
                <label htmlFor="isVisible" className="text-sm font-semibold text-gray-700">
                  Visible sur le site
                </label>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Créer le témoignage
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false)
                  setFormData({ firstName: '', position: '', company: '', testimonial: '', rating: 5, isVisible: true, logo: '', website: '' })
                }}
                className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-all"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Testimonials List */}
      <div className="space-y-4">
        {testimonials.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-600">Aucun témoignage pour le moment</p>
          </div>
        ) : (
          testimonials.map((testimonial: Testimonial) => (
            <div
              key={testimonial._id}
              className={`bg-white border-2 rounded-xl p-6 ${
                testimonial.isVisible ? 'border-teal-600' : 'border-gray-300'
              }`}
            >
              {editingId === testimonial._id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom</label>
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Poste</label>
                      <input
                        type="text"
                        value={editForm.position}
                        onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Entreprise</label>
                      <input
                        type="text"
                        value={editForm.company}
                        onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Logo</label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-600 transition-colors">
                              <Upload className="w-5 h-5 text-gray-500" />
                              <span className="text-sm text-gray-600">{uploadingEdit ? 'Upload en cours...' : 'Uploader un logo'}</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleEditLogoUpload}
                              className="hidden"
                              disabled={uploadingEdit}
                            />
                          </label>
                        </div>
                        {editForm.logo && (
                          <div className="flex items-center gap-2">
                            <img src={editForm.logo} alt="Logo" className="w-12 h-12 object-contain rounded border border-gray-200" />
                            <button
                              type="button"
                              onClick={() => setEditForm({ ...editForm, logo: '' })}
                              className="text-red-600 text-sm hover:underline"
                            >
                              Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Site web</label>
                      <input
                        type="url"
                        value={editForm.website}
                        onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                        placeholder="https://exemple.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Témoignage</label>
                    <textarea
                      value={editForm.testimonial}
                      onChange={(e) => setEditForm({ ...editForm, testimonial: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none resize-none"
                      rows={4}
                    />
                  </div>

                  <div className="flex items-center gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setEditForm({ ...editForm, rating: star })}
                            className="p-1 hover:scale-110 transition-transform"
                          >
                            <Star
                              className={`w-6 h-6 ${
                                star <= (editForm.rating || 5)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`edit-visible-${testimonial._id}`}
                        checked={editForm.isVisible}
                        onChange={(e) => setEditForm({ ...editForm, isVisible: e.target.checked })}
                        className="w-5 h-5 text-teal-600 rounded focus:ring-teal-600"
                      />
                      <label htmlFor={`edit-visible-${testimonial._id}`} className="text-sm font-semibold text-gray-700">
                        Visible sur le site
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => saveEdit(testimonial._id)}
                      className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-5 h-5" />
                      Enregistrer
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-all"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        {testimonial.logo && (
                          <img src={testimonial.logo} alt={testimonial.company} className="w-16 h-16 object-contain" />
                        )}
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-xl font-bold text-gray-900">{testimonial.firstName}</h3>
                            {testimonial.rating && (
                              <div className="flex gap-1">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{testimonial.position}</p>
                          <p className="text-lg font-semibold text-gray-900">{testimonial.company}</p>
                          {testimonial.website && (
                            <a href={testimonial.website} target="_blank" rel="noopener noreferrer" className="text-teal-600 text-sm hover:underline">
                              {testimonial.website}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleVisibility(testimonial)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          testimonial.isVisible
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {testimonial.isVisible ? 'Visible' : 'Masqué'}
                      </button>
                      <button
                        onClick={() => startEdit(testimonial)}
                        className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(testimonial._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.testimonial}"</p>
                  <p className="text-gray-400 text-sm mt-3">
                    Créé le {new Date(testimonial.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
